import { useEffect, useState } from 'react';
import { Button, HStack, Spacer, Text, VStack } from '@chakra-ui/react';
import { TrackingLabelProps } from '../../types/global';
import { getDataTracking } from '../../utils';

interface PaginationProps extends TrackingLabelProps {
  totalCount: number;
  pageSize?: number;
  pageLinksCount?: number;
  onPageChange: (page: number) => void;
}

export const Pagination = (props: PaginationProps) => {
  const {
    totalCount,
    onPageChange,
    pageSize = 5,
    pageLinksCount,
    trackingLabel,
  } = props;
  const [page, setPage] = useState(1);
  const lastPage = Math.ceil(totalCount / pageSize);
  const maxPageLinks = Math.min(pageLinksCount ?? 6, lastPage);
  const midPage = Math.floor(maxPageLinks / 2);

  const [pageLinks, setPageLinks] = useState<number[]>([]);

  const buildPageLinks = (pageNumber: number): number[] => {
    let firstLink = 1;
    if (pageNumber > midPage + 1) {
      firstLink = pageNumber - midPage;
    }
    if (lastPage - pageNumber < maxPageLinks / 2) {
      firstLink = lastPage - (maxPageLinks - 1);
    }

    // Construct an array of page numbers
    // Example maxPageLinks is 6; first page is 3
    //  => 3, 4, 5, 6, 7, 8
    return Array.from(Array(maxPageLinks).keys()).map(
      (pageNum) => pageNum + firstLink
    );
  };

  useEffect(() => {
    onPageChange(page);
    setPageLinks(buildPageLinks(page));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // The total count has changed. This means the data is filtered or modified.
  // The page number is no longer valid, so reset to 1st page.
  useEffect(() => {
    setPage(1);
    setPageLinks(buildPageLinks(1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  const next = (): void => {
    setPage((p) => (p < lastPage ? p + 1 : lastPage));
  };
  const previous = (): void => {
    setPage((p) => (p > 1 ? p - 1 : 1));
  };

  const isCurrentPage = (link: number): boolean => page === link;
  return (
    <VStack align="stretch" spacing={3}>
      <HStack spacing={3} justify="center">
        {pageLinks.map((pageLink) => (
          <Text
            fontWeight={isCurrentPage(pageLink) ? 'bold' : 'normal'}
            key={pageLink}
            onClick={() => setPage(pageLink)}
            _hover={{ cursor: 'pointer' }}
          >
            {pageLink}
          </Text>
        ))}
      </HStack>

      <HStack>
        <Button
          colorScheme="blue"
          onClick={previous}
          variant="secondary"
          visibility={page > 1 ? 'visible' : 'hidden'}
          w="100px"
          {...getDataTracking(`${trackingLabel}previousButton`)}
        >
          &lt; Previous
        </Button>

        <Spacer />

        <Button
          colorScheme="blue"
          onClick={next}
          variant="secondary"
          visibility={page < lastPage ? 'visible' : 'hidden'}
          w="100px"
          {...getDataTracking(`${trackingLabel}nextButton`)}
        >
          Next &gt;
        </Button>
      </HStack>
    </VStack>
  );
};
